import { lazy } from "react";
import type { RouteConfig } from "../config";
import { ROUTES } from "../paths";

const HrDashboardPage = lazy(() =>
  import("@/pages/hr/hr-dashboard-page").then((m) => ({ default: m.HrDashboardPage }))
);
const ColaboradoresPage = lazy(() =>
  import("@/pages/hr/employees-page").then((m) => ({ default: m.ColaboradoresPage }))
);
const DepartamentosPage = lazy(() =>
  import("@/pages/hr/departments-page").then((m) => ({ default: m.DepartamentosPage }))
);
const TimeClockPage = lazy(() =>
  import("@/pages/hr/time-tracking-page").then((m) => ({ default: m.TimeClockPage }))
);
const PrimaveraRulesPage = lazy(() =>
  import("@/pages/hr/primavera-rules-page").then((m) => ({ default: m.PrimaveraRulesPage }))
);
const BonusesPage = lazy(() =>
  import("@/pages/hr/bonuses-page").then((m) => ({ default: m.BonusesPage }))
);
const TimeBankPage = lazy(() =>
  import("@/pages/hr/time-bank-page").then((m) => ({ default: m.TimeBankPage }))
);
const MyTimeBankPage = lazy(() =>
  import("@/pages/hr/my-time-bank-page").then((m) => ({ default: m.MyTimeBankPage }))
);
const WorkforceMapPage = lazy(() =>
  import("@/pages/hr/workforce-map-page").then((m) => ({ default: m.WorkforceMapPage }))
);
const TurnosPage = lazy(() =>
  import("@/pages/hr/shifts-page").then((m) => ({ default: m.TurnosPage }))
);
const ScheduleChangeRequestsPage = lazy(() =>
  import("@/pages/hr/schedule-change-requests-page").then((m) => ({ default: m.ScheduleChangeRequestsPage }))
);
const DeclarationsPage = lazy(() =>
  import("@/pages/hr/declarations-page").then((m) => ({ default: m.DeclarationsPage }))
);
const OccupationalHealthPage = lazy(() =>
  import("@/pages/hr/occupational-health-page").then((m) => ({ default: m.OccupationalHealthPage }))
);
const VacationCalendarPage = lazy(() =>
  import("@/pages/hr/vacation-calendar-page").then((m) => ({ default: m.VacationCalendarPage }))
);
const SickLeavesPage = lazy(() =>
  import("@/pages/hr/sick-leave-page").then((m) => ({ default: m.SickLeavesPage }))
);
const UserDetailPage = lazy(() =>
  import("@/pages/users/detail-page").then((m) => ({
    default: m.UserDetailPage,
  }))
);

// Training System
const TrainingsPage = lazy(() =>
  import("@/pages/hr/trainings/trainings-page").then((m) => ({ default: m.TrainingsPage }))
);
const TrainingPlanPage = lazy(() =>
  import("@/pages/hr/trainings/training-plan-page").then((m) => ({ default: m.TrainingPlanPage }))
);
const TrainingDetailPage = lazy(() =>
  import("@/pages/hr/trainings/training-detail-page").then((m) => ({ default: m.TrainingDetailPage }))
);
const MyTrainingsPage = lazy(() =>
  import("@/pages/hr/trainings/my-trainings-page").then((m) => ({ default: m.MyTrainingsPage }))
);
const TrainingPlayerPage = lazy(() =>
  import("@/pages/hr/trainings/training-player-page").then((m) => ({ default: m.TrainingPlayerPage }))
);
const KnowledgeBasePage = lazy(() =>
  import("@/pages/hr/knowledge-base/knowledge-base-page").then((m) => ({ default: m.KnowledgeBasePage }))
);
const HrKpisPage = lazy(() =>
  import("@/pages/hr/hr-kpis-page").then((m) => ({ default: m.HrKpisPage }))
);
const VagasPage = lazy(() =>
  import("@/pages/hr/vagas-page").then((m) => ({ default: m.VagasPage }))
);
const CandidaturasPage = lazy(() =>
  import("@/pages/hr/candidaturas-page").then((m) => ({ default: m.CandidaturasPage }))
);
const VagaDetailPage = lazy(() =>
  import("@/pages/hr/vaga-detail-page").then((m) => ({ default: m.VagaDetailPage }))
);
const ApplicationReplySettingsPage = lazy(() =>
  import("@/pages/hr/application-reply-settings-page").then((m) => ({
    default: m.ApplicationReplySettingsPage,
  }))
);
const JobFunctionsPage = lazy(() =>
  import("@/pages/hr/job-functions-page").then((m) => ({ default: m.JobFunctionsPage }))
);
const CommunicationsPage = lazy(() =>
  import("@/pages/hr/communications-page").then((m) => ({ default: m.CommunicationsPage }))
);
const TalentPoolsPage = lazy(() =>
  import("@/pages/hr/talent-pools-page").then((m) => ({ default: m.TalentPoolsPage }))
);
const KnowledgeArticlePage = lazy(() =>
  import("@/pages/hr/knowledge-base/knowledge-article-page").then((m) => ({ default: m.KnowledgeArticlePage }))
);
const KnowledgeArticleEditPage = lazy(() =>
  import("@/pages/hr/knowledge-base/knowledge-article-edit-page").then((m) => ({ default: m.KnowledgeArticleEditPage }))
);
const OnboardingPage = lazy(() =>
  import("@/pages/onboarding/onboarding-page").then((m) => ({ default: m.OnboardingPage }))
);

// Internal Mobility
const InternalJobsPage = lazy(() =>
  import("@/pages/hr/internal-mobility/internal-jobs-page").then((m) => ({ default: m.InternalJobsPage }))
);
const InternalJobDetailPage = lazy(() =>
  import("@/pages/hr/internal-mobility/internal-job-detail-page").then((m) => ({ default: m.InternalJobDetailPage }))
);
const TalentBankPage = lazy(() =>
  import("@/pages/hr/internal-mobility/talent-bank-page").then((m) => ({ default: m.TalentBankPage }))
);
const MyTalentProfilePage = lazy(() =>
  import("@/pages/hr/internal-mobility/my-talent-profile-page").then((m) => ({ default: m.MyTalentProfilePage }))
);

// Calendars
// Pessoal, Equipa, Empresa, Salas e Vista Geral são camadas do Calendário do
// núcleo (topo da sidebar); os endereços antigos continuam a funcionar e levam
// à camada respetiva. O Mapa de Férias fica aqui: é uma vista de RH.
const CalendarRedirect = lazy(() =>
  import("@/pages/calendar/calendar-redirect").then((m) => ({ default: m.CalendarRedirect }))
);

const USER_READ        = { access: "permission" as const, entity: "users",         action: "READ" };
const BONUSES_READ     = { access: "permission" as const, entity: "bonuses",        action: "READ" };
const TIME_BANK_READ   = { access: "permission" as const, entity: "time_bank",      action: "READ", feature: "hr.time_bank" as const };
const TIME_BANK_ME     = { access: "authenticated" as const, feature: "hr.time_bank" as const };
const DEPT_READ        = { access: "permission" as const, entity: "departments",   action: "READ" };
const TRAINING_READ    = { access: "permission" as const, entity: "trainings",     action: "READ" };
const TRAINING_CREATE  = { access: "permission" as const, entity: "trainings",     action: "CREATE" };
const KB_READ          = { access: "permission" as const, entity: "knowledge_base",action: "READ" };
const KB_CREATE        = { access: "permission" as const, entity: "knowledge_base",action: "CREATE" };
const JOB_FUNC_READ    = { access: "permission" as const, entity: "job_functions", action: "READ" };
const COMMUNICATIONS_READ = { access: "permission" as const, entity: "internal_communications", action: "READ" };
const OCC_HEALTH_READ  = { access: "permission" as const, entity: "occupational_health", action: "READ" };
const TALENT_POOL_READ = { access: "permission" as const, entity: "talent_pools",  action: "READ" };
const VAGAS_READ       = { access: "permission" as const, entity: "vagas",         action: "READ" };
const INT_JOBS_READ    = { access: "permission" as const, entity: "internal_jobs", action: "READ" };
const AUTHENTICATED    = { access: "authenticated" as const };

// Lazy redirect components for removed pages (bookmarks / old links still work)
const RedirectToCalendar = lazy(() =>
  import("react-router-dom").then((m) => ({
    default: () => <m.Navigate to={ROUTES.HR.VACATION_CALENDAR} replace />,
  }))
);
const RedirectToVagas = lazy(() =>
  import("react-router-dom").then((m) => ({
    default: () => <m.Navigate to={ROUTES.HR.VAGAS} replace />,
  }))
);

export const hrRoutes: RouteConfig[] = [
  { path: ROUTES.HR.DASHBOARD, component: HrDashboardPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.KPIS, component: HrKpisPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.VAGAS, component: VagasPage, layout: "dashboard", permissions: VAGAS_READ },
  // Antes de VAGA_DETAIL, senão "resposta-automatica" é apanhado como :vagaId.
  {
    path: ROUTES.HR.APPLICATION_REPLY_SETTINGS,
    component: ApplicationReplySettingsPage,
    layout: "dashboard",
    permissions: VAGAS_READ,
  },
  { path: ROUTES.HR.VAGA_DETAIL, component: VagaDetailPage, layout: "dashboard", permissions: VAGAS_READ },
  { path: ROUTES.HR.CANDIDATURAS, component: CandidaturasPage, layout: "dashboard", permissions: VAGAS_READ },
  { path: ROUTES.HR.JOB_FUNCTIONS, component: JobFunctionsPage, layout: "dashboard", permissions: JOB_FUNC_READ },
  { path: ROUTES.HR.COMMUNICATIONS, component: CommunicationsPage, layout: "dashboard", permissions: COMMUNICATIONS_READ },
  { path: ROUTES.HR.TALENT_POOLS, component: TalentPoolsPage, layout: "dashboard", permissions: TALENT_POOL_READ },
  { path: ROUTES.HR.EMPLOYEES, component: ColaboradoresPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.EMPLOYEE_DETAIL, component: UserDetailPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.DEPARTMENTS, component: DepartamentosPage, layout: "dashboard", permissions: DEPT_READ },
  { path: ROUTES.HR.TIME_TRACKING, component: TimeClockPage, layout: "dashboard", permissions: { access: "custom", customCheck: (user) => user.isSuperuser || user.permissions.some((p) => p.entity === "users" && p.actions.includes("READ")) || user.departments.some((d) => d.representatives?.some((r) => r.id === user.id)) } },
  { path: ROUTES.HR.BONUSES, component: BonusesPage, layout: "dashboard", permissions: BONUSES_READ },
  { path: ROUTES.HR.TIME_BANK, component: TimeBankPage, layout: "dashboard", permissions: TIME_BANK_READ },
  { path: ROUTES.HR.MY_TIME_BANK, component: MyTimeBankPage, layout: "dashboard", permissions: TIME_BANK_ME },
  { path: ROUTES.HR.PRIMAVERA_RULES, component: PrimaveraRulesPage, layout: "dashboard", permissions: { access: "admin" } },
  { path: ROUTES.HR.WORKFORCE_MAP, component: WorkforceMapPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.SHIFTS, component: TurnosPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.SCHEDULE_CHANGE_REQUESTS, component: ScheduleChangeRequestsPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.DECLARATIONS, component: DeclarationsPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.OCCUPATIONAL_HEALTH, component: OccupationalHealthPage, layout: "dashboard", permissions: OCC_HEALTH_READ },
  { path: ROUTES.HR.VACATION_CALENDAR, component: VacationCalendarPage, layout: "dashboard", permissions: USER_READ },
  { path: ROUTES.HR.CALENDAR_OVERVIEW, component: CalendarRedirect, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.CALENDAR_INDIVIDUAL, component: CalendarRedirect, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.CALENDAR_TEAM, component: CalendarRedirect, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.CALENDAR_COMPANY, component: CalendarRedirect, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.CALENDAR_MEETING_ROOMS, component: CalendarRedirect, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.SICK_LEAVE, component: SickLeavesPage, layout: "dashboard", permissions: AUTHENTICATED },

  // Redirects for removed pages — bookmarks and old links still work
  { path: ROUTES.HR.COMPANY_CALENDAR, component: RedirectToCalendar, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.VACATION_MAP, component: RedirectToCalendar, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.CANDIDATOS, component: RedirectToVagas, layout: "dashboard", permissions: AUTHENTICATED },

  // Training System (CREATE before DETAIL so /new doesn't match :trainingId)
  { path: ROUTES.TRAININGS.PLAN, component: TrainingPlanPage, layout: "dashboard", permissions: TRAINING_READ },
  { path: ROUTES.TRAININGS.LIST, component: TrainingsPage, layout: "dashboard", permissions: TRAINING_READ },
  { path: ROUTES.TRAININGS.MY_TRAININGS, component: MyTrainingsPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.TRAININGS.CREATE, component: TrainingDetailPage, layout: "dashboard", permissions: TRAINING_CREATE },
  { path: ROUTES.TRAININGS.PLAYER, component: TrainingPlayerPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.TRAININGS.DETAIL, component: TrainingDetailPage, layout: "dashboard", permissions: TRAINING_READ },

  // Knowledge Base (EDIT before DETAIL so /edit doesn't match :articleId)
  { path: ROUTES.KNOWLEDGE_BASE.LIST, component: KnowledgeBasePage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.KNOWLEDGE_BASE.EDIT, component: KnowledgeArticleEditPage, layout: "dashboard", permissions: KB_CREATE },
  { path: ROUTES.KNOWLEDGE_BASE.DETAIL, component: KnowledgeArticlePage, layout: "dashboard", permissions: KB_READ },

  // Onboarding
  { path: ROUTES.ONBOARDING, component: OnboardingPage, layout: "public", permissions: AUTHENTICATED },

  // Internal Mobility
  { path: ROUTES.HR.INTERNAL_JOBS, component: InternalJobsPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.INTERNAL_JOB_DETAIL_PARAM, component: InternalJobDetailPage, layout: "dashboard", permissions: AUTHENTICATED },
  { path: ROUTES.HR.TALENT_BANK, component: TalentBankPage, layout: "dashboard", permissions: INT_JOBS_READ },
  { path: ROUTES.HR.MY_TALENT_PROFILE, component: MyTalentProfilePage, layout: "dashboard", permissions: AUTHENTICATED },
];
